import {
  createColumnHelper,
  createPaginatedRowModel,
  createSortedRowModel,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown, Inbox } from 'lucide-react'
import { type ReactNode } from 'react'

import { ErrorSurface } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ApiError } from '@/lib/problem-details'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

/**
 * Where a column goes below `lg`, where the table stops being a table.
 *
 * A data table on a narrow screen has two honest shapes and this is the one that is not a
 * horizontal scrollbar: each row becomes a card, the columns that identify it lead, and the rest
 * become labelled pairs. Naming the roles on the column rather than rebuilding the screen is what
 * lets a table that has never heard of a phone still produce a readable card.
 *
 *   `title`     the card's heading — one per table
 *   `lead`      identifying detail, on the line under the heading, unlabelled
 *   `secondary` a labelled pair in the card's detail grid — the default
 *   `hidden`    dropped from the card; the value is still in the table at `lg` and above
 */
export type DataTableCardRole = 'title' | 'lead' | 'secondary' | 'hidden'

/** Per-column presentation hints. Money and counts are right-aligned wherever they appear. */
export interface DataTableColumnMeta {
  align?: 'left' | 'right' | 'center'
  /** Fixed width, e.g. `w-32`, for columns whose content length is known. */
  widthClass?: string
  /** Keeps the column from wrapping — account numbers and dates read badly across two lines. */
  noWrap?: boolean
  /**
   * The column's place in the card layout. Left unsaid across a whole table, the first column
   * becomes the heading, the second joins it, and everything else is a labelled pair — which is
   * why an existing table needs no changes to stack sensibly.
   */
  card?: DataTableCardRole
  /** The label for the card's pair, for a column whose header is not plain text. */
  cardLabel?: string
}

/**
 * Sorting and pagination are registered but always driven manually: the server owns paging and
 * ordering because a branch's ledger will not fit in a browser, and because a page of rows the
 * caller may not see must never be filtered client-side.
 */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  // The assertion is the type-only slot that gives `columnDef.meta` its shape across every table
  // in the application; without it the slot would be typed as the empty object it is at runtime.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  columnMeta: {} as DataTableColumnMeta,
})

/**
 * A column's cell value type varies per column while the table only cares about the row type,
 * which is exactly the case TanStack's own generics use `any` for.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableColumn<TData extends RowData> = ColumnDef<typeof dataTableFeatures, TData, any>

/** Column helper bound to this table's feature set, for typed accessor and display columns. */
export const dataTableColumns = <TData extends RowData>() =>
  createColumnHelper<typeof dataTableFeatures, TData>()

const EMPTY_ROWS: never[] = []

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200]

export interface DataTableProps<TData extends RowData> {
  columns: DataTableColumn<TData>[]
  data: TData[] | undefined
  /** Total rows matching the query on the server, not the length of this page. */
  rowCount: number | undefined
  pagination: PaginationState
  onPaginationChange: OnChangeFn<PaginationState>
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  isLoading?: boolean
  /** A background refetch: the table keeps its rows and shows a quiet activity strip. */
  isFetching?: boolean
  error?: ApiError | null
  onRetry?: () => void
  /** Stable row identity, so selection and focus survive a refetch. */
  getRowId?: (row: TData) => string
  /**
   * Enter, Space, or a double click on a row; a single tap on a card. Rows are only focusable
   * when this is supplied.
   */
  onRowActivate?: (row: TData) => void
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: ReactNode
  pageSizeOptions?: number[]
  className?: string
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  rowCount,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  isLoading = false,
  isFetching = false,
  error = null,
  onRetry,
  getRowId,
  onRowActivate,
  emptyTitle = 'Nothing to show',
  emptyDescription = 'No records match the current filters.',
  emptyAction,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className,
}: DataTableProps<TData>) {
  const table = useTable({
    features: dataTableFeatures,
    columns,
    data: data ?? EMPTY_ROWS,
    state: { pagination, sorting },
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    rowCount: rowCount ?? 0,
    getRowId,
  })

  // One shape is rendered, not two hidden behind each other: a table that is present but display
  // none still answers the row-navigation selectors, and its cells still render.
  //
  // `lg`, the same threshold the sidebar and the refusal screens use. Between `md` and `lg` the
  // sidebar is already a drawer while the table was still a table, and the fixed column widths
  // that make an account number readable at desktop width had nowhere to go — a customer number,
  // a phone, and three status columns claim most of 768px before a name is placed. One breakpoint
  // for "is this the narrow layout" is also one question to answer when a new screen is added.
  const stacked = !useIsAtLeast('lg')

  const rows = table.getRowModel().rows
  const leafColumns = table.getAllLeafColumns()
  const columnCount = leafColumns.length
  const firstRowOnPage = pagination.pageIndex * pagination.pageSize + 1
  const lastRowOnPage = Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount ?? 0)

  const skeletonCount = Math.min(pagination.pageSize, stacked ? 5 : 8)

  const emptyPanel = (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <Inbox className="size-6 text-muted" aria-hidden />
      <p className="text-sm font-medium text-heading">{emptyTitle}</p>
      <p className="max-w-md text-xs text-muted">{emptyDescription}</p>
      {emptyAction}
    </div>
  )

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-card border border-border-subtle bg-surface',
        className,
      )}
    >
      <div className="relative">
        {isFetching && !isLoading ? (
          <div
            className="absolute inset-x-0 top-0 z-10 h-0.5 animate-pulse bg-brand-fill"
            aria-hidden
          />
        ) : null}

        {stacked ? (
          error ? (
            <ErrorSurface error={error} onRetry={onRetry} className="rounded-none border-0" />
          ) : isLoading ? (
            <ul className="divide-y divide-border-subtle">
              {Array.from({ length: skeletonCount }, (_, rowIndex) => (
                <li key={`skeleton-${String(rowIndex)}`} className="space-y-3 p-3">
                  <Skeleton className="h-4 w-2/3" />
                  <div className="grid grid-cols-2 gap-3">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                </li>
              ))}
            </ul>
          ) : rows.length === 0 ? (
            emptyPanel
          ) : (
            <ul className="divide-y divide-border-subtle">
              {rows.map((row) => (
                <li key={row.id}>
                  <RowCard
                    cells={row.getAllCells().map((cell) => ({
                      id: cell.id,
                      role: cardRole(cell.column.columnDef.meta, cell.column.id, leafColumns),
                      label: cardLabel(cell.column.columnDef, cell.column.id),
                      content: <table.FlexRender cell={cell} />,
                    }))}
                    onActivate={onRowActivate ? () => onRowActivate(row.original) : undefined}
                  />
                </li>
              ))}
            </ul>
          )
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta
                    const sortDirection = header.column.getIsSorted()

                    return (
                      <TableHead
                        key={header.id}
                        scope="col"
                        aria-sort={
                          sortDirection === 'asc'
                            ? 'ascending'
                            : sortDirection === 'desc'
                              ? 'descending'
                              : undefined
                        }
                        className={cn(
                          meta?.align === 'right' && 'text-right',
                          meta?.align === 'center' && 'text-center',
                          meta?.widthClass,
                        )}
                      >
                        {header.isPlaceholder ? null : header.column.getCanSort() ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className={cn(
                              'inline-flex items-center gap-1 rounded-sm text-2xs font-semibold tracking-wide uppercase transition-colors hover:text-heading',
                              meta?.align === 'right' && 'flex-row-reverse',
                            )}
                          >
                            <table.FlexRender header={header} />
                            {sortDirection === 'asc' ? (
                              <ArrowUp className="size-3" />
                            ) : sortDirection === 'desc' ? (
                              <ArrowDown className="size-3" />
                            ) : (
                              <ChevronsUpDown className="size-3 opacity-40" />
                            )}
                          </button>
                        ) : (
                          <table.FlexRender header={header} />
                        )}
                      </TableHead>
                    )
                  })}
                </tr>
              ))}
            </TableHeader>

            <TableBody>
              {error ? (
                <tr>
                  <TableCell colSpan={columnCount} className="p-0">
                    <ErrorSurface error={error} onRetry={onRetry} className="border-0" />
                  </TableCell>
                </tr>
              ) : isLoading ? (
                Array.from({ length: skeletonCount }, (_, rowIndex) => (
                  <TableRow key={`skeleton-${String(rowIndex)}`} className="hover:bg-transparent">
                    {leafColumns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton className="h-4 w-full max-w-40" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <TableCell colSpan={columnCount}>{emptyPanel}</TableCell>
                </tr>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    tabIndex={onRowActivate ? 0 : undefined}
                    role={onRowActivate ? 'button' : undefined}
                    // Single click opens the record — double-click-to-open read as "nothing
                    // happened" to a reviewer clicking once. Clicks that land on a real control
                    // inside a cell (a button, link or input) belong to that control, not the row.
                    onClick={
                      onRowActivate
                        ? (event) => {
                            const target = event.target as HTMLElement
                            if (target.closest('button, a, input, select, textarea, [role="menuitem"]')) {
                              return
                            }
                            if (window.getSelection()?.toString()) {
                              return
                            }
                            onRowActivate(row.original)
                          }
                        : undefined
                    }
                    onKeyDown={
                      onRowActivate
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              onRowActivate(row.original)
                            }
                          }
                        : undefined
                    }
                    className={cn(onRowActivate && 'cursor-pointer')}
                  >
                    {row.getAllCells().map((cell) => {
                      const meta = cell.column.columnDef.meta
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            meta?.align === 'right' && 'text-right',
                            meta?.align === 'center' && 'text-center',
                            meta?.noWrap && 'whitespace-nowrap',
                            meta?.widthClass,
                          )}
                        >
                          <table.FlexRender cell={cell} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-border-subtle bg-surface-muted px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <p className="numeric text-xs text-muted tabular-nums">
          {rowCount && rowCount > 0
            ? `Showing ${String(firstRowOnPage)}–${String(lastRowOnPage)} of ${String(rowCount)}`
            : 'No rows'}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-2 sm:justify-end sm:gap-3">
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-muted sm:inline">Rows</span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(next) => {
                table.setPagination({ pageIndex: 0, pageSize: Number(next) })
              }}
            >
              <SelectTrigger className="h-8 w-20 text-xs" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <span className="numeric text-xs text-muted tabular-nums">
            Page {pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}
          </span>

          <div className="flex items-center gap-1">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage() || isLoading}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage() || isLoading}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CardCell {
  id: string
  role: DataTableCardRole
  label: string
  content: ReactNode
}

/**
 * One row, as a card.
 *
 * The whole card is the target rather than a link inside it: a thumb aiming at a name on a moving
 * bus misses more often than it hits, and the row already means one thing.
 */
function RowCard({ cells, onActivate }: { cells: CardCell[]; onActivate?: () => void }) {
  const title = cells.find((cell) => cell.role === 'title')
  const leads = cells.filter((cell) => cell.role === 'lead')
  const pairs = cells.filter((cell) => cell.role === 'secondary')

  return (
    <div
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      onClick={onActivate}
      onKeyDown={
        onActivate
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onActivate()
              }
            }
          : undefined
      }
      className={cn(
        'flex min-h-11 flex-col gap-2 p-3 transition-colors',
        onActivate && 'cursor-pointer hover:bg-surface-hover',
      )}
    >
      {title || leads.length > 0 ? (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            {title ? (
              <div className="text-sm font-semibold break-words text-heading">{title.content}</div>
            ) : null}
            {leads.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                {leads.map((cell) => (
                  <span key={cell.id}>{cell.content}</span>
                ))}
              </div>
            ) : null}
          </div>
          {onActivate ? (
            <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
          ) : null}
        </div>
      ) : null}

      {pairs.length > 0 ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
          {pairs.map((cell) => (
            <div key={cell.id} className="min-w-0">
              <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                {cell.label}
              </dt>
              <dd className="mt-0.5 text-sm break-words text-body">{cell.content}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  )
}

/**
 * A table that says nothing about cards still has to produce one, so the first column leads and
 * the second joins it — which is the order a table already puts its identifying columns in.
 * Declaring a single role anywhere turns the guess off, because a table that has been thought
 * about should not also inherit a guess.
 */
function cardRole(
  meta: DataTableColumnMeta | undefined,
  columnId: string,
  leafColumns: { id: string; columnDef: { meta?: DataTableColumnMeta } }[],
): DataTableCardRole {
  if (meta?.card) {
    return meta.card
  }

  const anyDeclared = leafColumns.some((column) => column.columnDef.meta?.card !== undefined)
  if (anyDeclared) {
    // A table that named a `lead` but no `title` gets its first lead promoted, so the card is
    // never a grid of pairs with no heading over it.
    const declaredTitle = leafColumns.some((column) => column.columnDef.meta?.card === 'title')
    const firstLead = leafColumns.find((column) => column.columnDef.meta?.card === 'lead')
    return !declaredTitle && firstLead?.id === columnId ? 'title' : 'secondary'
  }

  const index = leafColumns.findIndex((column) => column.id === columnId)
  return index === 0 ? 'title' : index === 1 ? 'lead' : 'secondary'
}

function cardLabel(
  columnDef: { header?: unknown; meta?: DataTableColumnMeta },
  columnId: string,
): string {
  if (columnDef.meta?.cardLabel) {
    return columnDef.meta.cardLabel
  }

  return typeof columnDef.header === 'string' ? columnDef.header : columnId
}
